// ==================== ProcessSection.js ====================
import React from 'react';
import { CheckCircle, ArrowRight, Sparkles, Trophy } from 'lucide-react';

const ProcessSection = ({ activeStep, setActiveStep, isAutoPlaying, handleStepClick }) => {
  const steps = [
    { 
      number: "01", 
      title: "Discovery", 
      desc: "We learn about your goals, strengths, and dream roles through a comprehensive assessment.",
      features: ["Comprehensive career assessment", "Industry preference mapping", "Personalized job targeting"],
      gradient: "from-purple-500 to-blue-600"
    },
    { 
      number: "02", 
      title: "Optimization", 
      desc: "Complete LinkedIn and resume transformation with professional copywriting and design.",
      features: ["Professional headline optimization", "Experience section enhancement", "Skills and endorsements boost"],
      gradient: "from-purple-500 to-pink-600"
    },
    { 
      number: "03", 
      title: "Application", 
      desc: "Strategic job applications every hour and direct outreach to hiring managers at target companies.",
      features: ["Strategic job matching", "Direct hiring manager outreach", "Follow-up automation"],
      gradient: "from-green-500 to-purple-600"
    },
    { 
      number: "04", 
      title: "Success", 
      desc: "We schedule interviews and handle coordination with companies, plus preparation and negotiation support.",
      features: ["Interview preparation sessions", "Salary negotiation guidance", "Offer evaluation support"],
      gradient: "from-orange-500 to-purple-600"
    }
  ];

  return (
    <section id="section-1" className="min-h-screen flex items-center justify-center py-20 px-6 relative"
             style={{
               background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 25%, #581c87 50%, #1e1b4b 75%, #0f172a 100%)',
               backgroundSize: '400% 400%',
               animation: 'gradientShift 15s ease infinite'
             }}>
      {/* Floating orbs */}
      <div className="absolute top-20 left-20 w-72 h-72 rounded-full opacity-10"
           style={{
             background: 'radial-gradient(circle, rgba(6, 182, 212, 0.1) 0%, transparent 70%)',
             animation: 'float 6s ease-in-out infinite'
           }}></div>
      <div className="absolute top-60 right-20 w-48 h-48 rounded-full opacity-10"
           style={{
             background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)',
             animation: 'float 8s ease-in-out infinite reverse'
           }}></div>

      <div className="max-w-7xl mx-auto w-full">
        <div className="text-center mb-20">
          <h2 className="text-5xl lg:text-6xl font-bold mb-6 text-white relative">
            How it{' '}
            <span style={{
              background: 'linear-gradient(45deg, #ec4899, #8b5cf6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              works
            </span>
            {isAutoPlaying && (
              <div className="absolute -top-2 -right-4 bg-gradient-to-r from-green-500 to-purple-600 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse">
                Auto ▶
              </div>
            )}
          </h2>
          <p className="text-xl text-white/70 max-w-2xl mx-auto">
            Four simple steps to your dream job
          </p>
        </div>

        {/* Step Navigation */}
        <div className="flex justify-center mb-16 flex-wrap gap-2">
          {steps.map((step, index) => (
            <button
              key={index}
              onClick={() => handleStepClick(index)}
              className="px-8 py-4 rounded-full font-semibold transition-all duration-300 flex items-center gap-3 relative overflow-hidden"
              style={{
                background: activeStep === index 
                  ? 'linear-gradient(45deg, #ec4899, #8b5cf6)' 
                  : 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(20px)',
                border: activeStep === index ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                color: 'white'
              }}
            >
              <span 
                className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold"
                style={{
                  background: activeStep === index 
                    ? 'rgba(255, 255, 255, 0.2)' 
                    : 'linear-gradient(45deg, #ec4899, #8b5cf6)'
                }}
              >
                {index + 1}
              </span>
              {step.title}
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="rounded-3xl p-16 min-h-[400px]"
             style={{
               background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
               backdropFilter: 'blur(20px)',
               border: '1px solid rgba(255, 255, 255, 0.1)',
               boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)'
             }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div>
              <div 
                className="w-20 h-20 rounded-2xl flex items-center justify-center text-3xl font-bold text-white mb-8"
                style={{
                  background: `linear-gradient(45deg, ${steps[activeStep].gradient.includes('purple-500') ? '#a855f7' : steps[activeStep].gradient.includes('green-500') ? '#22c55e' : steps[activeStep].gradient.includes('orange-500') ? '#f97316' : '#a855f7'}, ${steps[activeStep].gradient.includes('blue-600') ? '#2563eb' : steps[activeStep].gradient.includes('pink-600') ? '#db2777' : '#a855f7'})`,
                  boxShadow: `0 12px 32px ${steps[activeStep].gradient.includes('purple-500') ? '#a855f7' : steps[activeStep].gradient.includes('green-500') ? '#22c55e' : steps[activeStep].gradient.includes('orange-500') ? '#f97316' : '#a855f7'}40`
                }}
              >
                {steps[activeStep].number}
              </div>
              
              <h3 className="text-4xl font-bold text-white mb-6 leading-tight">
                {steps[activeStep].title}
              </h3>
              
              <p className="text-xl text-white/80 leading-relaxed mb-8">
                {steps[activeStep].desc}
              </p>

              <div className="space-y-4 mb-8">
                {steps[activeStep].features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle size={20} className="text-green-400" />
                    <span className="text-white/90">{feature}</span>
                  </div>
                ))}
              </div>

              <button 
                className="flex items-center gap-2 px-10 py-4 rounded-2xl font-semibold transition-all duration-300 hover:scale-105"
                style={{
                  background: 'linear-gradient(45deg, #06b6d4, #8b5cf6)',
                  color: 'white',
                  boxShadow: '0 8px 32px rgba(139, 92, 246, 0.3)'
                }}
              >
                Learn More
                <ArrowRight size={18} />
              </button>
            </div>

            {/* Right Visual */}
            <div className="flex justify-center relative">
              <div className="relative">
                <div 
                  className="w-80 h-96 rounded-3xl p-1 shadow-2xl"
                  style={{ background: 'linear-gradient(135deg, #1f2937, #374151)' }}
                >
                  <div 
                    className="w-full h-full rounded-3xl p-8 overflow-hidden relative"
                    style={{ background: 'linear-gradient(135deg, #0f172a, #1e1b4b)' }}
                  >
                    {/* Phone UI content based on active step */}
                    {activeStep === 0 && (
                      <div>
                        <div className="text-white text-lg font-semibold mb-6">Career Assessment</div>
                        <div className="bg-white/10 rounded-xl p-4 mb-4">
                          <div className="text-cyan-400 text-sm mb-2">What's your dream role?</div>
                          <div className="text-white">Software Engineer</div>
                        </div>
                        <div className="bg-white/10 rounded-xl p-4 mb-4">
                          <div className="text-cyan-400 text-sm mb-2">Preferred industry?</div>
                          <div className="text-white">Tech Startups</div>
                        </div>
                        <div 
                          className="rounded-xl p-4 text-white text-center text-sm font-semibold"
                          style={{ background: 'linear-gradient(45deg, #10b981, #06b6d4)' }}
                        >
                          Continue Assessment
                        </div>
                      </div>
                    )}
                    {activeStep === 1 && (
                      <div>
                        <div className="text-white text-lg font-semibold mb-6">LinkedIn Optimization</div>
                        <div className="bg-white/10 rounded-xl p-4 mb-4">
                          <div className="text-purple-400 text-sm mb-2">New Headline</div>
                          <div className="text-white text-sm leading-relaxed">🚀 Full-Stack Developer | React & Node.js Expert | Building Amazing User Experiences</div>
                        </div>
                        <div className="bg-white/10 rounded-xl p-4 mb-4">
                          <div className="text-purple-400 text-sm mb-2">Profile Views</div>
                          <div className="text-green-400 text-xl font-semibold">+245% ↗️</div>
                        </div>
                        <div 
                          className="rounded-xl p-4 text-white text-center text-sm font-semibold"
                          style={{ background: 'linear-gradient(45deg, #8b5cf6, #ec4899)' }}
                        >
                          View Optimized Profile
                        </div>
                      </div>
                    )}
                    {activeStep === 2 && (
                      <div>
                        <div className="text-white text-lg font-semibold mb-6">Job Applications</div>
                        <div className="bg-white/10 rounded-xl p-4 mb-3">
                          <div className="flex justify-between items-center">
                            <div>
                              <div className="text-white font-semibold text-sm">Google</div>
                              <div className="text-white/70 text-xs">Frontend Engineer</div>
                            </div>
                            <div className="bg-green-500 text-white px-2 py-1 rounded-lg text-xs font-semibold">Applied</div>
                          </div>
                        </div>
                        <div className="bg-white/10 rounded-xl p-4 mb-3">
                          <div className="flex justify-between items-center">
                            <div>
                              <div className="text-white font-semibold text-sm">Stripe</div>
                              <div className="text-white/70 text-xs">Full Stack Developer</div>
                            </div>
                            <div className="bg-yellow-500 text-white px-2 py-1 rounded-lg text-xs font-semibold">Interview</div>
                          </div>
                        </div>
                        <div className="text-cyan-400 text-sm font-semibold text-center">
                          12 applications this week 🎯
                        </div>
                      </div>
                    )}
                    {activeStep === 3 && (
                      <div>
                        <div className="text-white text-lg font-semibold mb-6">Job Offer! 🎉</div>
                        <div 
                          className="rounded-xl p-5 mb-4 text-white"
                          style={{ background: 'linear-gradient(45deg, #10b981, #06b6d4)' }}
                        >
                          <div className="font-semibold mb-2">Congratulations!</div>
                          <div className="text-sm mb-3">You've received a job offer from TechCorp</div>
                          <div className="text-2xl font-bold">$95,000/year</div>
                        </div>
                        <div className="bg-white/10 rounded-xl p-4 mb-4">
                          <div className="text-yellow-400 text-sm mb-2">Our Success Fee</div>
                          <div className="text-white font-semibold">$2,850 (3% of salary)</div>
                        </div>
                        <div 
                          className="rounded-xl p-4 text-white text-center text-sm font-semibold"
                          style={{ background: 'linear-gradient(45deg, #f97316, #ef4444)' }}
                        >
                          Accept Offer
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Floating elements */}
                <div 
                  className="absolute top-5 right-5 rounded-xl p-3"
                  style={{
                    background: 'linear-gradient(45deg, #06b6d4, #8b5cf6)',
                    animation: 'float 3s ease-in-out infinite'
                  }}
                >
                  <Sparkles size={24} className="text-white" />
                </div>
                <div 
                  className="absolute bottom-5 left-5 rounded-xl p-3"
                  style={{
                    background: 'linear-gradient(45deg, #ec4899, #f97316)',
                    animation: 'float 4s ease-in-out infinite reverse'
                  }}
                >
                  <Trophy size={24} className="text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>


       {/* Progress Navigation */}
       <div className="flex justify-center items-center gap-6 mt-10">
          <button
            onClick={() => handleStepClick(Math.max(0, activeStep - 1))}
            disabled={activeStep === 0}
            className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
              activeStep === 0 
                ? 'bg-white/10 text-white/30 cursor-not-allowed' 
                : 'bg-white/10 hover:bg-white/20 text-white border border-white/20 hover:scale-105'
            }`}
          >
            ← Previous
          </button>
          
          <div className="flex gap-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className="w-3 h-3 rounded-full transition-all duration-300"
                style={{
                  background: index === activeStep 
                    ? 'linear-gradient(45deg, #ec4899, #8b5cf6)' 
                    : 'rgba(255, 255, 255, 0.3)',
                  transform: index === activeStep ? 'scale(1.25)' : 'scale(1)'
                }}
              />
            ))}
          </div>

          <button
            onClick={() => handleStepClick(Math.min(steps.length - 1, activeStep + 1))}
            disabled={activeStep === steps.length - 1}
            className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
              activeStep === steps.length - 1 
                ? 'bg-white/10 text-white/30 cursor-not-allowed' 
                : 'bg-white/10 hover:bg-white/20 text-white border border-white/20 hover:scale-105'
            }`}
          >
            Next →
          </button>
        </div>
        
      </div>
    </section>
  );
};

export default ProcessSection;